import { Loader2 } from "lucide-react";
import { type ChangeEvent, useState } from "react";
import { Link } from "react-router";

import { AppLayout } from "@/components/layout/app-layout";
import { getModuleStatus, type Module, MODULES } from "@/data/modules";
import { useFacilitatorState, useFacilitatorStateWriter } from "@/lib/api";

function statusLabel(status: ReturnType<typeof getModuleStatus>): string {
  if (status === "completed") {
    return "Completed";
  }
  if (status === "current") {
    return "Current";
  }
  return "Upcoming";
}

// ---------------------------------------------------------------------------
// Miro boards — main board + one per team, persisted per-browser
// ---------------------------------------------------------------------------

export const FACILITATOR_MIRO_KEYS = [
  { key: "fac-miro-main", label: "Main board" },
  { key: "fac-miro-team-1", label: "Team 1" },
  { key: "fac-miro-team-2", label: "Team 2" },
  { key: "fac-miro-team-3", label: "Team 3" },
  { key: "fac-miro-team-4", label: "Team 4" },
];

function MiroLinkRow({ storageKey, label }: { storageKey: string; label: string }) {
  const { data: state } = useFacilitatorState();
  const { setKey } = useFacilitatorStateWriter();
  const stored = (state?.[storageKey] as string | undefined) ?? "";
  const [editing, setEditing] = useState<boolean>(!stored);
  const [draft, setDraft] = useState<string>(stored);

  const save = (): void => {
    const trimmed = draft.trim();
    if (trimmed) {
      setKey(storageKey, trimmed);
      setEditing(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => setDraft(e.target.value);

  if (!editing && stored) {
    const display = stored.length > 46 ? stored.slice(0, 43) + "…" : stored;
    return (
      <div className="flex items-center gap-3 py-2">
        <span className="text-muted w-20 flex-shrink-0 text-xs font-semibold tracking-wide uppercase">
          {label}
        </span>
        <a
          href={stored}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary flex-1 truncate text-sm hover:underline"
        >
          {display}
        </a>
        <button
          type="button"
          onClick={() => {
            setDraft(stored);
            setEditing(true);
          }}
          className="text-muted hover:text-foreground flex-shrink-0 text-xs font-medium transition-colors"
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-2">
      <span className="text-muted w-20 flex-shrink-0 text-xs font-semibold tracking-wide uppercase">
        {label}
      </span>
      <input
        type="url"
        value={draft}
        onChange={handleChange}
        onBlur={save}
        onKeyDown={(e) => e.key === "Enter" && save()}
        placeholder="https://miro.com/app/board/..."
        className="border-border focus:ring-primary flex-1 rounded-lg border px-3 py-1.5 text-sm outline-none focus:ring-2"
      />
      <button
        type="button"
        onClick={save}
        className="bg-primary flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
      >
        Save
      </button>
    </div>
  );
}

function FacilitatorMiroLinks() {
  const { isLoading } = useFacilitatorState();
  return (
    <div className="border-border mb-8 rounded-2xl border bg-white p-5">
      <p className="text-foreground mb-1 text-sm font-semibold">Miro boards</p>
      <p className="text-muted mb-2 text-xs">
        One shared main board plus a board per team — shared with all facilitators.
      </p>
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="text-muted h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="divide-border divide-y">
          {FACILITATOR_MIRO_KEYS.map(({ key, label }) => (
            <MiroLinkRow key={key} storageKey={key} label={label} />
          ))}
        </div>
      )}
    </div>
  );
}

function ModuleCard({ module }: { module: Module }) {
  const status = getModuleStatus(module);
  const isCurrent = status === "current";
  const totalPrep = module.facilitator.checklist.reduce((s, c) => s + c.items.length, 0);

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-2xl bg-white shadow-md transition-shadow hover:shadow-lg ${
        isCurrent ? "ring-primary ring-2" : ""
      }`}
    >
      <div className={`h-1.5 w-full ${isCurrent ? "bg-primary" : "bg-muted/30"}`} />

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-2 flex items-start justify-between gap-2">
          <span className="text-muted text-xs font-semibold tracking-wider uppercase">
            Module {module.id}
          </span>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              isCurrent
                ? "bg-primary text-white"
                : status === "completed"
                  ? "bg-muted/20 text-muted"
                  : "border-border text-muted border"
            }`}
          >
            {statusLabel(status)}
          </span>
        </div>

        <h3 className="font-heading text-foreground text-base leading-snug font-bold">
          {module.title}
        </h3>
        <p className="text-muted mt-0.5 text-xs">
          {module.location} · {module.month}
        </p>

        <p className="text-muted mt-3 text-xs">
          <span className="text-foreground font-semibold">{totalPrep}</span> prep items
        </p>

        <div className="mt-4 flex flex-col gap-2">
          <Link
            to={`/facilitator/modules/${module.id}?tab=agenda`}
            className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
              isCurrent
                ? "bg-primary hover:bg-primary/90 text-white"
                : "bg-primary/10 text-primary hover:bg-primary/20"
            }`}
          >
            <span>View agenda</span>
            <span>&#8594;</span>
          </Link>

          <div className="flex gap-2">
            <Link
              to={`/facilitator/modules/${module.id}?tab=prepare`}
              className="border-border text-muted hover:text-foreground flex-1 rounded-lg border px-2 py-1.5 text-center text-xs font-medium transition-colors"
            >
              Prep
            </Link>
            <Link
              to={`/facilitator/modules/${module.id}?tab=run`}
              className="border-border text-muted hover:text-foreground flex-1 rounded-lg border px-2 py-1.5 text-center text-xs font-medium transition-colors"
            >
              Run
            </Link>
            <Link
              to={`/facilitator/modules/${module.id}?tab=materials`}
              className="border-border text-muted hover:text-foreground flex-1 rounded-lg border px-2 py-1.5 text-center text-xs font-medium transition-colors"
            >
              Materials
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function FacilitatorOverviewPage() {
  return (
    <AppLayout>
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-primary mb-1 text-xs font-semibold tracking-[0.24em] uppercase">
              Tesla Program · Facilitator
            </p>
            <h1 className="font-heading text-foreground text-3xl font-bold md:text-4xl">
              Facilitator Hub
            </h1>
            <p className="text-muted mt-2 text-sm">
              Agendas, prep checklists, run guides, and slide decks for all four modules.
            </p>
          </div>
          <Link
            to="/facilitator/planning"
            className="bg-primary/10 text-primary hover:bg-primary/20 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors"
          >
            Program Planning &#8594;
          </Link>
        </div>

        <FacilitatorMiroLinks />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {MODULES.map((module) => (
            <ModuleCard key={module.id} module={module} />
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
